import { useState, useEffect } from 'react';
import { Coins, RefreshCw, Clock, ShieldCheckIcon, TrophyIcon } from 'lucide-react';
import { differenceInSeconds } from 'date-fns';
import EarlyVotingBonusTooltip from './EarlyVotingBonusTooltip';

interface CashPrizeInfoProps {
  totalVotes: number;
  endsAt: string;
}

export default function CashPrizeInfo({ totalVotes, endsAt }: CashPrizeInfoProps) {
  const [timeRemaining, setTimeRemaining] = useState('');
  const [earlyBonus, setEarlyBonus] = useState<number>(100);
  const prizePerVote = 0.05; // $0.05 per vote
  const totalPrize = (totalVotes * prizePerVote).toFixed(2);
  const isEarlyAdopter = true; // This would be determined based on user signup date

  useEffect(() => {
    // Function to update the countdown and early bonus
    const updateCountdown = () => {
      const endDate = new Date(endsAt);
      const now = new Date();
      
      // Get the start date from the database
      // We calculate it based on the end date and the actual duration
      // This is more accurate than assuming a fixed 7-day duration
      const startDate = new Date(endDate);
      // We need to go back in time to when the SNAP was created
      // This is the difference between now and the end date, plus the time that has passed
      const totalDurationMs = endDate.getTime() - now.getTime();
      const elapsedMs = now.getTime() - startDate.getTime() + totalDurationMs;
      startDate.setTime(endDate.getTime() - elapsedMs);
      
      if (now >= endDate) {
        setTimeRemaining('Closed');
        setEarlyBonus(0);
        return;
      }
      
      const secondsDiff = differenceInSeconds(endDate, now);
      
      // Calculate days, hours, minutes, seconds
      const days = Math.floor(secondsDiff / 86400);
      const hours = Math.floor((secondsDiff % 86400) / 3600);
      const minutes = Math.floor((secondsDiff % 3600) / 60);
      const seconds = Math.floor(secondsDiff % 60);
      
      // Format the time remaining
      setTimeRemaining(`${days}d ${hours}h ${minutes}m ${seconds}s`);
      
      // Calculate early bonus percentage
      const totalDuration = differenceInSeconds(endDate, startDate);
      const elapsed = differenceInSeconds(now, startDate);
      const percentageElapsed = (elapsed / totalDuration) * 100;
      
      // Apply tiered bonus based on when vote would be cast
      if (percentageElapsed <= 10) {
        setEarlyBonus(100); // First 10% of duration: 100% of points
      } else if (percentageElapsed <= 30) {
        setEarlyBonus(75); // 10-30% of duration: 75% of points
      } else if (percentageElapsed <= 60) {
        setEarlyBonus(50); // 30-60% of duration: 50% of points
      } else if (percentageElapsed <= 90) {
        setEarlyBonus(25); // 60-90% of duration: 25% of points
      } else {
        setEarlyBonus(5); // Last 10% of duration: 5% of points
      }
    };
    
    // Update immediately and then every second
    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    
    // Clean up interval on unmount
    return () => clearInterval(interval);
  }, [endsAt]);

  return (
    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
      <div className="space-y-2">
        <div className="flex items-center text-yellow-800 font-semibold">
          <Coins className="h-5 w-5 mr-2 text-yellow-600" />
          <span className="text-lg">CASH PRIZE: ${totalPrize}</span>
        </div>
        
        <div className="flex items-center text-yellow-700 text-sm">
          <RefreshCw className="h-4 w-4 mr-2 text-yellow-600" />
          <span>Based on {totalVotes} votes – Each vote adds ${prizePerVote.toFixed(2)}</span>
        </div>
        
        <div className="flex items-center text-yellow-700 text-sm">
          <Clock className="h-4 w-4 mr-2 text-yellow-600" />
          <span>Closes in: {timeRemaining}</span>
        </div>
        
        <div className="flex items-center text-yellow-700 text-sm">
          <EarlyVotingBonusTooltip earlyBonus={earlyBonus} />
        </div>

        {isEarlyAdopter && (
          <div className="flex items-center text-yellow-700 text-sm">
            <TrophyIcon className="h-4 w-4 mr-2 text-yellow-600" />
            <span>Early adopter bonus: <span className="font-semibold">+25%</span> on activity score</span>
          </div>
        )}

        <div className="flex items-center text-yellow-700 text-sm mt-2 bg-blue-50 p-2 rounded-lg border border-blue-100">
          <ShieldCheckIcon className="h-4 w-4 mr-2 text-blue-600" />
          <span>Age verification required (18+) for withdrawals</span>
        </div>
      </div>
    </div>
  );
}